/**
 * Sticky มุมมอง + เดือนรายงาน for /report nurse_notes.
 * Writes shell nav state. ReportShellNurseNotesPeriodSync copies that into the report filters
 * so charts, the scoreboard, and the table follow one choice.
 * This component renders in AuthenticatedLayout, above SupervisorReportingFiltersProvider.
 */
import { useNavShellFilters, type ReportNurseNotesView } from '../contexts/NavShellFiltersContext';
import type { ReportingMonth } from '../contexts/SupervisorReportingFiltersContext';
import {
  fiscalQuarterOptionLabel,
  fiscalYearQuarterToAnchorReportingMonth,
  reportingMonthFromCalendarParts,
  reportingMonthToFiscalQuarterAnchor,
  reportingMonthToFiscalYearQuarter,
  type FiscalQuarter,
} from '../lib/reportingMonth';
import { THAI_LONG_MONTHS } from '../utils/dateThai';

const controlClass =
  'mt-0.5 min-h-9 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-sm text-slate-800';

const yearOptions = Array.from({ length: 11 }, (_value, index) => new Date().getFullYear() - 5 + index);

function buddhistYearLabel(gregorianYear: number): string {
  return `พ.ศ. ${gregorianYear + 543}`;
}

function reportingMonthParts(month: string): { year: number; monthIndex: number } {
  const [yearPart, monthPart] = month.split('-').map(Number);
  const year = Number.isFinite(yearPart) ? yearPart : new Date().getFullYear();
  const monthIndex = Number.isFinite(monthPart) ? Math.min(11, Math.max(0, monthPart - 1)) : 0;
  return { year, monthIndex };
}

export function NurseNotesShellPeriodControls() {
  const {
    reportNurseNotesView,
    setReportNurseNotesView,
    reportNurseNotesReportingMonth,
    setReportNurseNotesReportingMonth,
  } = useNavShellFilters();

  const { year, monthIndex } = reportingMonthParts(reportNurseNotesReportingMonth);

  const handleViewChange = (nextView: ReportNurseNotesView) => {
    setReportNurseNotesView(nextView);
    if (nextView === 'year') {
      setReportNurseNotesReportingMonth(`${year}-01` as ReportingMonth);
      return;
    }
    if (nextView === 'trimester') {
      setReportNurseNotesReportingMonth(reportingMonthToFiscalQuarterAnchor(reportNurseNotesReportingMonth));
    }
  };

  return (
    <div className="pointer-events-auto flex flex-wrap items-end gap-3">
      <label className="flex flex-col text-xs font-medium text-slate-600">
        มุมมอง
        <select
          className={controlClass}
          value={reportNurseNotesView}
          aria-label="มุมมอง"
          onChange={(event) => handleViewChange(event.target.value as ReportNurseNotesView)}
        >
          <option value="monthly">รายเดือน</option>
          <option value="trimester">รายไตรมาส</option>
          <option value="year">รายปี</option>
        </select>
      </label>
      {reportNurseNotesView === 'year' ? (
        <label className="flex flex-col text-xs font-medium text-slate-600">
          ปี (พ.ศ.)
          <select
            className={controlClass}
            aria-label="ปีรายงาน"
            value={reportNurseNotesReportingMonth.substring(0, 4)}
            onChange={(event) => setReportNurseNotesReportingMonth(`${event.target.value}-01` as ReportingMonth)}
          >
            {yearOptions.map((optionYear) => (
              <option key={optionYear} value={optionYear}>
                {buddhistYearLabel(optionYear)}
              </option>
            ))}
          </select>
        </label>
      ) : reportNurseNotesView === 'trimester' ? (
        <>
          <label className="flex flex-col text-xs font-medium text-slate-600">
            ปี (พ.ศ.)
            <select
              className={controlClass}
              aria-label="ปีรายงาน"
              value={reportingMonthToFiscalYearQuarter(reportNurseNotesReportingMonth).fiscalYear}
              onChange={(event) => {
                const { quarter } = reportingMonthToFiscalYearQuarter(reportNurseNotesReportingMonth);
                setReportNurseNotesReportingMonth(
                  fiscalYearQuarterToAnchorReportingMonth(Number(event.target.value), quarter),
                );
              }}
            >
              {yearOptions.map((optionYear) => (
                <option key={optionYear} value={optionYear}>
                  {buddhistYearLabel(optionYear)}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col text-xs font-medium text-slate-600">
            ไตรมาส
            <select
              className={controlClass}
              aria-label="ไตรมาส"
              value={reportingMonthToFiscalYearQuarter(reportNurseNotesReportingMonth).quarter}
              onChange={(event) => {
                const quarter = Number(event.target.value) as FiscalQuarter;
                const { fiscalYear } = reportingMonthToFiscalYearQuarter(reportNurseNotesReportingMonth);
                setReportNurseNotesReportingMonth(fiscalYearQuarterToAnchorReportingMonth(fiscalYear, quarter));
              }}
            >
              {([1, 2, 3, 4] as const).map((quarter) => (
                <option key={quarter} value={quarter}>
                  {fiscalQuarterOptionLabel(
                    reportingMonthToFiscalYearQuarter(reportNurseNotesReportingMonth).fiscalYear,
                    quarter,
                  )}
                </option>
              ))}
            </select>
          </label>
        </>
      ) : (
        <>
          <label className="flex flex-col text-xs font-medium text-slate-600">
            ปี (พ.ศ.)
            <select
              className={controlClass}
              aria-label="ปีรายงาน"
              value={year}
              onChange={(event) =>
                setReportNurseNotesReportingMonth(
                  reportingMonthFromCalendarParts(Number(event.target.value), monthIndex),
                )
              }
            >
              {yearOptions.map((optionYear) => (
                <option key={optionYear} value={optionYear}>
                  {buddhistYearLabel(optionYear)}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col text-xs font-medium text-slate-600">
            เดือน
            <select
              className={controlClass}
              aria-label="เดือนรายงาน"
              value={monthIndex}
              onChange={(event) =>
                setReportNurseNotesReportingMonth(
                  reportingMonthFromCalendarParts(year, Number(event.target.value)),
                )
              }
            >
              {THAI_LONG_MONTHS.map((monthName, index) => (
                <option key={monthName} value={index}>
                  {monthName}
                </option>
              ))}
            </select>
          </label>
        </>
      )}
    </div>
  );
}
